import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MessageCircle, ShieldCheck, Send, CheckCircle2, AlertCircle } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { CtaWhatsApp, WhatsAppIcon } from "../components/Cta";
import { Reveal } from "../components/motion";
import { waLink, DEFAULT_WA_MESSAGE, WHATSAPP_DISPLAY, RESPONSE_TIME, DELIVERY_DELAY } from "../config/site";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const inputCls =
  "w-full rounded-xl border border-nuit-900/15 bg-white px-4 py-3 text-sm text-nuit-900 placeholder:text-steel/60 transition-colors focus:border-electric focus:outline-none";

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [t0] = useState(() => Date.now());
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [serverError, setServerError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Indiquez votre nom (2 caractères minimum).";
    if (!EMAIL_RE.test(form.email)) errs.email = "Indiquez une adresse e-mail valide.";
    if (form.phone && !/^[0-9+\s().-]{6,30}$/.test(form.phone)) errs.phone = "Numéro de téléphone invalide.";
    if (form.message.trim().length < 10) errs.message = "Votre message est un peu court (10 caractères minimum).";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStatus("sending");
    setServerError("");
    try {
      const res = await fetch(`${BACKEND}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, t0 }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Une erreur est survenue.");
      }
      setStatus("sent");
    } catch (err) {
      setServerError(err.message);
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-emerald-500/30 bg-emerald-50 p-8 text-center" data-testid="contact-form-success">
        <CheckCircle2 className="h-10 w-10 text-emerald-600" />
        <p className="mt-4 font-display text-xl font-bold text-nuit-900">Message bien reçu !</p>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-steel">
          Nous revenons vers vous très vite. Pour une réponse encore plus rapide ({RESPONSE_TIME.toLowerCase()}),
          WhatsApp reste le canal le plus direct.
        </p>
        <CtaWhatsApp label="Nous contacter sur WhatsApp" className="mt-6" testid="contact-success-cta" />
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-nuit-900/10 bg-white p-7 sm:p-8" data-testid="contact-form" noValidate>
      <h3 className="font-display text-lg font-bold text-nuit-900">Ou par e-mail, si vous préférez</h3>
      <p className="mt-1.5 text-sm text-steel">
        WhatsApp reste le canal le plus rapide — mais ce formulaire fonctionne aussi.
      </p>

      {/* Honeypot anti-spam : invisible pour les humains */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="ct-name" className="mb-1.5 block text-sm font-semibold text-nuit-900">Nom *</label>
          <input id="ct-name" className={inputCls} value={form.name} onChange={set("name")}
            placeholder="Votre nom" data-testid="contact-name-input" maxLength={100} />
          {errors.name && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="ct-email" className="mb-1.5 block text-sm font-semibold text-nuit-900">E-mail *</label>
          <input id="ct-email" type="email" className={inputCls} value={form.email} onChange={set("email")}
            placeholder="vous@exemple.fr" data-testid="contact-email-input" />
          {errors.email && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{errors.email}</p>}
        </div>
      </div>
      <div className="mt-4">
        <label htmlFor="ct-phone" className="mb-1.5 block text-sm font-semibold text-nuit-900">Téléphone (optionnel)</label>
        <input id="ct-phone" type="tel" className={inputCls} value={form.phone} onChange={set("phone")}
          placeholder="+33 6 12 34 56 78" data-testid="contact-phone-input" maxLength={30} />
        {errors.phone && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{errors.phone}</p>}
      </div>
      <div className="mt-4">
        <label htmlFor="ct-message" className="mb-1.5 block text-sm font-semibold text-nuit-900">Votre message *</label>
        <textarea id="ct-message" rows={5} className={`${inputCls} resize-y`} value={form.message} onChange={set("message")}
          placeholder="Parlez-nous de votre activité et de votre projet de carte…" data-testid="contact-message-input" maxLength={2000} />
        {errors.message && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{errors.message}</p>}
      </div>

      {status === "error" && (
        <p className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 p-3.5 text-sm font-medium text-red-700" role="alert" data-testid="contact-form-error">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {serverError} Vous pouvez aussi nous écrire sur WhatsApp.
        </p>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        data-testid="contact-form-submit"
        className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-nuit-900 px-6 py-3.5 font-display text-sm font-semibold text-white transition-[background-color,opacity] duration-300 hover:bg-nuit-800 disabled:opacity-50"
      >
        <Send className="h-4 w-4" />
        {status === "sending" ? "Envoi en cours…" : "Envoyer mon message"}
      </button>
      <p className="mt-3 text-center text-xs leading-relaxed text-steel">
        Vos données servent uniquement à vous répondre — voir notre{" "}
        <Link to="/politique-de-confidentialite" className="font-medium text-electric underline-offset-2 hover:underline">
          politique de confidentialité
        </Link>.
      </p>
    </form>
  );
}

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact — Réponse sous 30 minutes"
        description="Une question ou un projet de carte digitale ? Écrivez à Noveocard sur WhatsApp au +33 7 68 22 96 11 (réponse sous 30 minutes) ou via le formulaire de contact."
        path="/contact"
      />

      <PageHero
        dark
        eyebrow="Contact"
        titleLines={["Parlons de", "votre projet."]}
        description="Une question ou un projet ? Écrivez-nous directement sur WhatsApp. Réponse sous 30 minutes."
      />

      <section className="bg-brume py-20 sm:py-24">
        <div className="container-nv grid gap-6 lg:grid-cols-[1.3fr_1fr]">
          <Reveal>
            <div className="grain relative h-full overflow-hidden rounded-3xl bg-nuit-900 p-8 text-white sm:p-12">
              <WhatsAppIcon className="h-10 w-10 text-electric-300" />
              <h2 className="mt-6 font-display text-2xl font-bold sm:text-3xl">
                WhatsApp est notre canal prioritaire — et notre fierté.
              </h2>
              <p className="mt-4 max-w-lg leading-relaxed text-white/70">
                Pas de ticket en attente : une vraie conversation avec une vraie personne, du premier
                message à la livraison de vos cartes.
              </p>
              <div className="mt-8 flex flex-col items-start gap-4">
                <CtaWhatsApp
                  label="Nous contacter sur WhatsApp"
                  message={DEFAULT_WA_MESSAGE}
                  size="lg"
                  showResponseTime
                  testid="contact-cta-whatsapp"
                />
                <a
                  href={waLink(DEFAULT_WA_MESSAGE)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-display text-xl font-bold tracking-wide text-white underline-offset-4 hover:underline"
                  data-testid="contact-phone-link"
                >
                  {WHATSAPP_DISPLAY}
                </a>
              </div>
              <div className="mt-10 grid gap-4 border-t border-white/10 pt-8 sm:grid-cols-3">
                {[
                  { icon: Clock, t: RESPONSE_TIME, d: "Pendant les horaires d'ouverture, jamais d'attente interminable." },
                  { icon: MessageCircle, t: "7 à 10 jours ouvrés", d: DELIVERY_DELAY },
                  { icon: ShieldCheck, t: "Sans engagement", d: "Vous validez le visuel avant toute production. Aucun paiement en ligne." },
                ].map((c) => (
                  <div key={c.t}>
                    <c.icon className="h-5 w-5 text-electric-300" />
                    <p className="mt-2 font-display text-sm font-bold">{c.t}</p>
                    <p className="mt-1 text-xs leading-relaxed text-white/60">{c.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
